import ComponentManager from "../../../componentFramework";
import PrimeFacesUtil, {WidgetListener} from "../PrimeFacesUtil";
import EventListenerManager from "../../EventListenerManager";


/**
 */
@ComponentManager.register()
export default class PrimeFacesInplace {
	public static componentStyleClass = "ui-inplace";
	private readonly eventListenerManager = new EventListenerManager();

	constructor(private readonly rootElement: HTMLElement) {
		PrimeFacesUtil.addDeferredRender(
			this.getPrimeFacesWidgetId(),
			rootElement.id,
			this.onPrimeFacesDeferredRender
		);

		const input = this.getInput();
		if (input !== null) {
			const managedInput = this.eventListenerManager.manage(input as HTMLElement);
			const managedContainerElem = this.eventListenerManager.manage(rootElement);
			managedContainerElem.addEventListener("click", this.onClick, true);
			managedContainerElem.addEventListener("mousedown", this.stopMouseEventPropagation, true);
			managedContainerElem.addEventListener("mouseup", this.stopMouseEventPropagation, true);

			managedInput.addEventListener("keydown", PrimeFacesInplace.onKeyDown);
			managedInput.addEventListener("keyup", this.onKeyUp);
			managedInput.addEventListener("blur", () => {
				this.save();
			});
			managedInput.addEventListener("dragstart", this.stopMouseEventPropagation, true);
			managedInput.addEventListener("dragend", this.stopMouseEventPropagation, true);
		}
	}

	@ComponentManager.on("ready")
	private componentReady() {
		// TODO - Why do I have this in an onReady while PrimeFacesTree does not? My guess is because inplace is added often after the page loads while the tree happens to be on the page on first load before the JS code runs. If that is the case, I should be using onReady in the other location too.... Why is onReady needed for this but not other stuff in the constructor? Cause reading data from the dom?
		// Disable all JQuery UI animations on this widget. This will override any settings done in the HTML too.
		PrimeFacesUtil.addWidgetListener("init", this.getPrimeFacesWidgetId(), this.onWidgetReady, true);
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		PrimeFacesUtil.removeDeferredRender(
			this.getPrimeFacesWidgetId(),
			this.rootElement.id,
			this.onPrimeFacesDeferredRender
		);
		PrimeFacesUtil.removeWidgetListener("init", this.getPrimeFacesWidgetId(), this.onWidgetReady);
		this.eventListenerManager.removeListeners();
	}

	private onWidgetReady: WidgetListener = widget => widget.cfg.effect = "none";

	/**
	 * Returns reference to the input or textarea element.
	 * Otherwise returns null.
	 */
	private getInput() {
		return this.rootElement.querySelector<HTMLInputElement|HTMLTextAreaElement>("input, textarea");
	}

	private readonly stopMouseEventPropagation = (ev: MouseEvent) => {
		// Stop a click on the input from going through to elements behind it.
		if (!this.isDisabled()) {
			ev.stopPropagation();
		}
	}
	
	private readonly onClick = (ev: MouseEvent) => {
		if (!this.isDisabled()) {
			ev.stopPropagation();
			if (this.isContentVisible() === false && this.isSaving() === false) {
				this.show();
			}
		}
	}

	public show() {
		this.getPrimeFacesWidget().show();
	}

	private static onKeyDown(ev: KeyboardEvent) {
		if (ev.key === "Enter" || ev.key === "Escape" || ev.key === "Esc"){
			ev.stopPropagation();
			ev.preventDefault();
		}
	}

	private readonly onKeyUp = (ev: KeyboardEvent) => {
		if (ev.key === "Escape") {
			this.cancel();
		} else if (ev.key === "Enter") {
			ev.preventDefault();
			ev.stopPropagation();
			this.save();
		}
	}

	/**
	 * This is called when the render happens which is deferred until the input becomes shown.
	 */
	private readonly onPrimeFacesDeferredRender = () => {
		// Set the selection to be all text minus the extension, if this is for a filename
		const input = this.getInput();
		if (input !== null) {
			if (this.rootElement.classList.contains("ui-inplace--filename")) {
				const indexOfPeriod = input.value.lastIndexOf(".");
				input.setSelectionRange(0, indexOfPeriod);
			} else {
				input.select();
			}
		}
	}


	private cancel() {
		const pfWidget = this.getPrimeFacesWidget();
		pfWidget.hide();
		pfWidget.cancel();
	}

	private save() {
		// Improve the visual feedback of this primefaces component
		// When saving hide the input immediately after updating the display
		// Then we call save. This will trigger ajax which will rerender again
		// but with this approach we are giving immediate feedback.
		// We also set a class of "isSaving" (removed when ajax updates) which
		// We use to set some styling with.

		const input = this.getInput();
		
		// There is no input field (which is an error; gracefully exit)
		if (input === null) {
			return;
		}

		// If the value is unchanged there is nothing to save (aka send back to server)
		// Otherwise mark as saving, do a quick visual update and start the savings (which happens via ajax)
		const pfWidget = this.getPrimeFacesWidget();
		const displayElems = pfWidget.getDisplay();
		pfWidget.hide();
		
		if (displayElems !== null && displayElems !== undefined && displayElems.length > 0) {
			const displayElem = displayElems[0]!;
			if (displayElem.innerText === input.value) {
				return;
			}
			displayElem.innerText = input.value;
		}
		this.rootElement.classList.add("isSaving");
		pfWidget.save();
	}

	

	private isSaving(): boolean {
		return this.rootElement.classList.contains("isSaving");
	}
	private isDisabled(): boolean {
		return this.rootElement.classList.contains("isDisabled");
	}
	private isContentVisible(): boolean {
		const contentElems = this.getPrimeFacesWidget().getContent();
		return contentElems !== null && contentElems !== undefined && contentElems.length !== 0 && contentElems[0]!.style.display !== "none";
	}

	private getPrimeFacesWidgetId() {
		const res = this.rootElement.getAttribute("data-widget");
		if (res === null) {
			throw `Unable to retrieve widget for ${this.rootElement}.`;
		}
		return res;
	}
	private getPrimeFacesWidget(): {
		save: ()=>{},
		cancel: ()=>{},
		show: ()=>{},
		hide: ()=>{},
		getDisplay: () => Array<HTMLElement>,
		getContent: () => Array<HTMLElement>,
		cfg: any
		// There are more methods on the returned object but this is all we need
	} { 
		const getPrimeFacesWidget = (window as any).PF; // Note: There is another way to get the widget if there is not 'dataset.widget' on the element. You can do this: PrimeFaces.getWidgetById(elem.id)
		return getPrimeFacesWidget(this.getPrimeFacesWidgetId());
	}

}
